export type InlineCitationType = 'chunk' | 'document' | 'web';

export interface SourceBlock {
	page: number; // 1-indexed page number
	bbox: number[]; // [x0, y0, x1, y1] in PDF coords (bottom-left origin)
}

/**
 * All display info needed for a single citation badge / highlight in the document panel.
 * Used by transformSourceText.
 */
export interface CitationHighlight {
	hash: string;
	citation_index: number;
	citation_color: string;
	sub_index: string;
	quotedText?: string;
	chunkText?: string;
	sourceBlocks?: SourceBlock[];
	/** chunkId for 'chunk', docId for 'document', webId for 'web' */
	id: number | string;
	type: InlineCitationType;
}

/**
 * Normalize Unicode characters to their canonical ASCII equivalents for matching.
 * This helps match quotes when LLMs use different Unicode variants than the source.
 */
function normalizeUnicodeForMatching(text: string): string {
	return (
		text
			// Normalize all hyphen/dash variants to regular hyphen-minus (U+002D)
			.replace(/[\u2010\u2011\u2012\u2013\u2014\u2015]/g, '-')
			// Normalize all space variants to regular space (U+0020)
			.replace(/[\u00A0\u202F\u2000-\u200B]/g, ' ')
			// Normalize quote marks to ASCII equivalents
			.replace(/[\u201C\u201D\u201E\u201F]/g, '"') // All double quotes to "
			.replace(/[\u2018\u2019\u201A\u201B]/g, "'") // All single quotes to '
			// Normalize other common variants
			.replace(/[\u00AB\u00BB]/g, '"')
	); // Guillemets to "
}

/**
 * Escape special regex characters in a string and replace whitespace with \s+ pattern.
 */
function escapeRegexPattern(text: string): string {
	return text
		.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // Escape special regex chars
		.replace(/\s+/g, '\\s+'); // Replace whitespace with \s+ pattern
}

/**
 * Find quote in text with whitespace-insensitive matching.
 * Returns the actual text from the source if found, or multiple ranges if ellipsis is present.
 */
function findQuoteInText(
	quote: string,
	text: string
):
	| { index: number; actualText: string }
	| { ranges: Array<{ start: number; end: number }> }
	| null {
	// Check if quote contains ellipsis
	const hasEllipsis = /\.{3}|…/.test(quote);

	if (hasEllipsis) {
		// Split by ellipsis (... or unicode ellipsis character)
		const parts = quote
			.split(/\.{3}|…/)
			.map((p) => p.trim())
			.filter((p) => p.length > 0);

		if (parts.length === 0) {
			return null;
		}

		// Find each part sequentially in the text
		const ranges: Array<{ start: number; end: number }> = [];
		let searchStartIndex = 0;

		for (const part of parts) {
			const remainingText = text.substring(searchStartIndex);
			const partMatch = findSingleQuotePart(part, remainingText);

			if (partMatch) {
				const absoluteStart = searchStartIndex + partMatch.index;
				const absoluteEnd = absoluteStart + partMatch.actualText.length;
				ranges.push({ start: absoluteStart, end: absoluteEnd });
				// Next search starts after this match
				searchStartIndex = absoluteEnd;
			} else {
				// Part not found - if we found at least one part, return what we have
				if (ranges.length > 0) {
					return { ranges };
				}

				// No parts found at all
				return null;
			}
		}

		// All parts found
		return { ranges };
	}

	// No ellipsis - use simple matching
	return findSingleQuotePart(quote, text);
}

/**
 * Find a single quote part (without ellipsis) in text.
 */
function findSingleQuotePart(
	quote: string,
	text: string
): { index: number; actualText: string } | null {
	// Strategy 1: Exact match
	const exactIndex = text.indexOf(quote);
	if (exactIndex !== -1) {
		return { index: exactIndex, actualText: quote };
	}

	// Strategy 2: Case-insensitive match
	const lowerText = text.toLowerCase();
	const lowerQuote = quote.toLowerCase();
	const caseInsensitiveIndex = lowerText.indexOf(lowerQuote);
	if (caseInsensitiveIndex !== -1) {
		const actualText = text.substring(caseInsensitiveIndex, caseInsensitiveIndex + quote.length);
		return { index: caseInsensitiveIndex, actualText };
	}

	// Strategy 3: Whitespace-insensitive regex match
	const escapedQuote = escapeRegexPattern(quote);
	const regex = new RegExp(escapedQuote, 'i');
	const match = text.match(regex);
	if (match && match.index !== undefined) {
		return { index: match.index, actualText: match[0] };
	}

	// Strategy 4: Unicode-normalized matching
	const normalizedQuote = normalizeUnicodeForMatching(quote.toLowerCase());
	const normalizedText = normalizeUnicodeForMatching(text.toLowerCase());
	const normalizedIndex = normalizedText.indexOf(normalizedQuote);
	if (normalizedIndex !== -1) {
		const actualText = text.substring(normalizedIndex, normalizedIndex + quote.length);
		return { index: normalizedIndex, actualText };
	}

	return null;
}

/**
 * Expand a text range to encompass the full sentence.
 */
function expandToFullSentence(
	text: string,
	startIndex: number,
	endIndex: number
): { start: number; end: number } {
	const sentenceEnders = /[.!?:;—]\s|[\n\r]/;
	const markdownBoundaries = /^[-*+#>][\s]|^```|^---+|^\*\*\*+/m;

	let sentenceStart = startIndex;
	for (let i = startIndex - 1; i >= 0; i--) {
		const twoChars = text.substring(i, i + 2);
		if (sentenceEnders.test(twoChars)) {
			const char = text[i];
			if (char === '\n' || char === '\r') {
				sentenceStart = i + 1;
			} else {
				sentenceStart = i + 2;
			}

			break;
		}

		const lineStart = text.lastIndexOf('\n', i) + 1;
		const lineContent = text.substring(lineStart, i + 1).trim();
		if (markdownBoundaries.test(lineContent)) {
			sentenceStart = i + 1;
			break;
		}

		if (i === 0) {
			sentenceStart = 0;
		}
	}

	let sentenceEnd = endIndex;
	for (let i = endIndex; i < text.length - 1; i++) {
		const twoChars = text.substring(i, i + 2);
		if (sentenceEnders.test(twoChars)) {
			sentenceEnd = i + 1;
			break;
		}
	}

	if (sentenceEnd === endIndex && endIndex < text.length) {
		sentenceEnd = text.length;
	}

	return { start: sentenceStart, end: sentenceEnd };
}

type ResolvedRange = { start: number; end: number; hash: string };
type MergedRange = { start: number; end: number; hashes: string[] };

function resolveRange(quote: CitationHighlight, text: string): ResolvedRange | null {
	// Strategy 1: quotedText — find and expand to full sentence
	if (quote.quotedText) {
		const match = findQuoteInText(quote.quotedText, text);
		if (match) {
			if ('ranges' in match) {
				// Ellipsis: expand each part, take overall bounding box
				const expandedRanges = match.ranges.map((r) => expandToFullSentence(text, r.start, r.end));
				return {
					start: Math.min(...expandedRanges.map((r) => r.start)),
					end: Math.max(...expandedRanges.map((r) => r.end)),
					hash: quote.hash
				};
			} else {
				const { start, end } = expandToFullSentence(
					text,
					match.index,
					match.index + match.actualText.length
				);
				return { start, end, hash: quote.hash };
			}
		}
	}

	// Strategy 2: full chunkText — find and expand to sentence from the start of the match
	if (quote.chunkText) {
		const idx = text.indexOf(quote.chunkText);
		if (idx !== -1) {
			const anchorEnd = Math.min(idx + 100, idx + quote.chunkText.length);
			const { start, end } = expandToFullSentence(text, idx, anchorEnd);
			return { start, end, hash: quote.hash };
		}
	}

	// Strategy 3: first N characters of chunkText
	const PREVIEW_LENGTH = 150;
	if (quote.chunkText) {
		const preview = quote.chunkText.substring(0, PREVIEW_LENGTH).trim();
		const idx = text.indexOf(preview);
		if (idx !== -1) {
			const { start, end } = expandToFullSentence(text, idx, idx + preview.length);
			return { start, end, hash: quote.hash };
		}
	}

	// No match found — drop this citation
	return null;
}

function mergeRanges(ranges: ResolvedRange[]): MergedRange[] {
	const sorted = [...ranges].sort((a, b) => a.start - b.start);
	const merged: MergedRange[] = [];

	for (const r of sorted) {
		if (merged.length === 0 || r.start > merged[merged.length - 1].end) {
			merged.push({ start: r.start, end: r.end, hashes: [r.hash] });
		} else {
			const last = merged[merged.length - 1];
			last.end = Math.max(last.end, r.end);
			if (!last.hashes.includes(r.hash)) {
				last.hashes.push(r.hash);
			}
		}
	}

	return merged;
}

/**
 * Transform source document text to highlight cited passages.
 * Resolves all quote ranges first, merges overlaps, then emits highlight tags in one pass.
 */
export function transformSourceText(text: string, quotes: CitationHighlight[]): string {
	// Phase 1: resolve each quote to a range
	const resolved = quotes.flatMap((q) => {
		const r = resolveRange(q, text);
		return r ? [r] : [];
	});

	if (resolved.length === 0) {
		return text.replace(/(?<!\n)\n(?!\n)/g, '\n\n');
	}

	// Phase 2: merge overlapping ranges
	const merged = mergeRanges(resolved);

	// Phase 3: build output (reverse order to preserve indices)
	let result = text;
	for (const r of [...merged].reverse()) {
		const matchedText = result.substring(r.start, r.end);
		const trimmedText = matchedText.trimEnd();
		const trailing = matchedText.slice(trimmedText.length);
		const tag = `<highlight hashes="${r.hashes.join(',')}">${trimmedText}</highlight>${trailing}`;
		result = result.substring(0, r.start) + tag + result.substring(r.end);
	}

	// Replace single newlines with double newlines for markdown (but preserve existing multiple newlines)
	const txt = result.replace(/(?<!\n)\n(?!\n)/g, '\n\n');
	return txt;
}
